// Sensor label resolver — the single place a raw field name becomes a display
// label (issue #4). It encapsulates the four-step cascade so every callsite —
// including pure view-models that can't call React hooks — resolves names the
// same way:
//
//   machine-specific override → company-global override → static built-in
//   label (fieldLabels.ts) → prettified raw key
//
// The tenant's overrides live in a module-level cache, loaded once on app
// entry via setCompanyOverrides (see useSensorMappings). This keeps the
// existing data/display separation intact: view-models keep calling a plain
// function, exactly like they already call fieldLabel().
//
// A `machineKey` is the data-plane identity "{company_slug}.{external}", the
// same key the machine store uses. Company-scoped contexts (no machine) resolve
// against the global tier only.

import { fieldLabel } from '@/fieldLabels';
import type { SensorMapping } from '@/features/sensors/api';

// field_name → label (company-global tier).
let globalOverrides: Map<string, string> = new Map();
// "{machineKey} {field_name}" → label (per-machine tier).
let machineOverrides: Map<string, string> = new Map();

const machineCacheKey = (machineKey: string, field: string) =>
  `${machineKey} ${field}`;

/** machineKey for a mapping row, or null for the company-global tier. */
function rowMachineKey(m: SensorMapping): string | null {
  return m.machine ? `${m.company_slug}.${m.machine}` : null;
}

/**
 * Load the tenant's full mapping set into the cache, replacing any prior tenant
 * (the admin company-switcher refetches and calls this with the new company's
 * rows). Idempotent — safe to call on every query settle.
 */
export function setCompanyOverrides(rows: SensorMapping[]): void {
  const g = new Map<string, string>();
  const m = new Map<string, string>();
  for (const row of rows) {
    if (!row.display_label) continue;
    const mk = rowMachineKey(row);
    if (mk) m.set(machineCacheKey(mk, row.field_name), row.display_label);
    else g.set(row.field_name, row.display_label);
  }
  globalOverrides = g;
  machineOverrides = m;
}

/** Drop all overrides (logout / company clear). */
export function clearCompanyOverrides(): void {
  globalOverrides = new Map();
  machineOverrides = new Map();
}

/**
 * Resolve a field to its display label. Pass machineKey for full two-tier
 * resolution; omit it for company-global + static only.
 */
export function resolveFieldLabel(field: string, machineKey?: string): string {
  if (machineKey) {
    const mv = machineOverrides.get(machineCacheKey(machineKey, field));
    if (mv) return mv;
  }
  const gv = globalOverrides.get(field);
  if (gv) return gv;
  // fieldLabel already covers the static built-in map + prettified-raw fallback.
  return fieldLabel(field);
}

/** True when a custom (non-static) name is set for this field at any tier that
 *  applies. Lets UIs distinguish "company renamed this" from the built-in. */
export function hasCustomLabel(field: string, machineKey?: string): boolean {
  if (machineKey && machineOverrides.has(machineCacheKey(machineKey, field))) return true;
  return globalOverrides.has(field);
}
